import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { User, Star, Lock } from 'lucide-react';
import { getMediationAreaLabel } from '@/lib/mediationAreaMapping';

export interface MediatorResultCardData {
  id?: string | null;
  is_verified?: boolean | null;
  rating?: number | null;
  mediation_areas?: string[] | null;
  info_meeting_price_cents?: number | null;
  info_meeting_duration_min?: number | null;
  mediation_meeting_total_price_cents?: number | null;
  mediation_meeting_duration_min?: number | null;
  profiles?: {
    full_name?: string | null;
    city?: string | null;
  } | null;
}

interface MediatorResultCardProps {
  mediator: MediatorResultCardData;
  /** When false, renders a lock icon and a "sign-in" CTA instead of "Voir le profil". */
  isAuthenticated?: boolean;
  /** Override the primary action (e.g., booking flow). */
  onAction?: () => void;
  actionLabel?: string;
  /** Hide the action button (e.g., when the card is the headline of a public profile page). */
  hideAction?: boolean;
}

export const MediatorResultCard = ({
  mediator,
  isAuthenticated = true,
  onAction,
  actionLabel,
  hideAction = false,
}: MediatorResultCardProps) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onAction) {
      onAction();
      return;
    }
    if (!isAuthenticated) {
      navigate('/connexion?mode=signup');
      return;
    }
    if (mediator.id) {
      navigate(`/mediateur/${mediator.id}`);
    }
  };

  const ctaLabel =
    actionLabel ?? (isAuthenticated ? 'Voir le profil' : 'Se connecter pour voir');

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-2">
          <div>
            <h4 className="font-semibold flex items-center gap-2">
              <User className="h-4 w-4" />
              {mediator.profiles?.full_name}
              {!isAuthenticated && (
                <Lock className="h-3 w-3 text-muted-foreground" />
              )}
            </h4>
            <p className="text-xs text-muted-foreground">{mediator.profiles?.city}</p>
          </div>
          {mediator.is_verified && (
            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">✓</span>
          )}
        </div>

        <div className="space-y-1 mb-3">
          {mediator.mediation_areas && mediator.mediation_areas.length > 0 && (
            <p className="text-xs">
              <strong>Domaines:</strong>{' '}
              {mediator.mediation_areas.slice(0, 2).map(getMediationAreaLabel).join(', ')}
              {mediator.mediation_areas.length > 2 && '...'}
            </p>
          )}
          {mediator.info_meeting_price_cents != null && (
            <div className="flex flex-wrap gap-1 mt-1">
              <Badge variant="outline" className="text-[10px] font-normal">
                1er RDV : {(mediator.info_meeting_price_cents / 100).toFixed(0)} € ·{' '}
                {mediator.info_meeting_duration_min ?? 30} min
              </Badge>
              <Badge variant="outline" className="text-[10px] font-normal">
                Médiation :{' '}
                {((mediator.mediation_meeting_total_price_cents ?? 20000) / 200).toFixed(0)}{' '}
                €/partie · {mediator.mediation_meeting_duration_min ?? 60} min
              </Badge>
            </div>
          )}
          {mediator.rating != null && mediator.rating > 0 && (
            <div className="flex items-center gap-1">
              <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
              <span className="text-xs font-semibold">{mediator.rating.toFixed(1)}</span>
              <span className="text-xs text-muted-foreground">/ 5</span>
            </div>
          )}
        </div>

        {!hideAction && (
          <Button size="sm" onClick={handleClick} className="w-full">
            {ctaLabel}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
